(() => {
  if (!customElements.get('brazur-hover-image')) {
    customElements.define('brazur-hover-image', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'mouse' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
          this.resetPreview();
          this.hovering = true;
          this.hoverTimer = setTimeout(() => this.showPreview(), 600);
        });
        this.addEventListener('pointerleave', () => this.hidePreview());
        this.addEventListener('pointercancel', () => this.hidePreview());
      }
      disconnectedCallback() { this.resetPreview(); }
      resetPreview() {
        this.hovering = false;
        clearTimeout(this.hoverTimer);
        clearTimeout(this.removeTimer);
        this.preview?.remove();
        this.preview = null;
      }
      hidePreview() {
        this.hovering = false;
        clearTimeout(this.hoverTimer);
        const image = this.preview;
        if (!image) return;
        image.classList.remove('is-visible');
        this.removeTimer = setTimeout(() => {
          image.remove();
          if (this.preview === image) this.preview = null;
        }, 220);
      }
      showPreview() {
        if (!this.hovering || !this.isConnected) return;
        let src = this.dataset.hoverSrc;
        let source;
        if (this.dataset.hoverKind === 'catalog') {
          const productImage = this.querySelector('[data-bz-product-image]');
          const wornImage = this.querySelector('[data-bz-worn-image]');
          if (productImage?.hidden) source = productImage.querySelector('img');
          else if (wornImage && !wornImage.dataset.failed) source = wornImage;
          if (!source) return;
          src = source.dataset.wornSrc || source.currentSrc || source.getAttribute('src');
        }
        if (!src) return;
        const image = document.createElement('img');
        image.className = 'bz-hover-preview';
        image.alt = '';
        image.setAttribute('aria-hidden', 'true');
        image.decoding = 'async';
        this.preview = image;
        const reveal = () => {
          requestAnimationFrame(() => {
            if (this.hovering && this.preview === image && this.isConnected) image.classList.add('is-visible');
          });
        };
        image.addEventListener('load', reveal, { once: true });
        image.addEventListener('error', () => {
          image.remove();
          if (this.preview === image) this.preview = null;
        }, { once: true });
        if (source?.getAttribute('srcset')) {
          image.setAttribute('srcset', source.getAttribute('srcset'));
          image.setAttribute('sizes', source.getAttribute('sizes') || '100vw');
        }
        const target = this.dataset.hoverKind === 'collection' ? this.querySelector('.collection-card__image') : this;
        if (!target) { this.preview = null; return; }
        target.append(image);
        image.src = src;
        if (image.complete && image.naturalWidth > 0) reveal();
      }
    });
  }

  if (!customElements.get('brazur-view-toggle')) {
    customElements.define('brazur-view-toggle', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.section = this.closest('.shopify-section');
        if (!this.section) return;
        this.initialized = true;
        this.mode = 'product';
        this.buttons = [...this.querySelectorAll('[data-view]')];
        for (const button of this.buttons) {
          button.addEventListener('click', () => this.setView(button.dataset.view));
        }
        this.hidden = false;
      }
      setView(mode) {
        if (!['product', 'worn'].includes(mode)) return;
        this.mode = mode;
        for (const media of this.section.querySelectorAll('brazur-hover-image')) media.resetPreview?.();
        for (const button of this.buttons) button.setAttribute('aria-pressed', String(button.dataset.view === mode));
        for (const card of this.section.querySelectorAll('[data-brazur-variant]')) {
          const productImage = card.querySelector('[data-bz-product-image]');
          const wornImage = card.querySelector('[data-bz-worn-image]');
          const fallback = card.querySelector('[data-bz-fallback]');
          if (!productImage) continue;
          const showWorn = mode === 'worn' && wornImage && !wornImage.dataset.failed;
          productImage.hidden = Boolean(showWorn);
          if (wornImage) {
            wornImage.hidden = !showWorn;
            if (showWorn && !wornImage.getAttribute('src')) {
              wornImage.addEventListener('error', () => {
                wornImage.dataset.failed = 'true';
                wornImage.hidden = true;
                productImage.hidden = false;
                if (fallback) fallback.hidden = this.mode !== 'worn';
              }, { once: true });
              wornImage.src = wornImage.dataset.wornSrc;
            }
          }
          if (fallback) fallback.hidden = mode !== 'worn' || Boolean(showWorn);
        }
        this.querySelector('[data-view-status]').textContent = mode === 'worn'
          ? (document.documentElement.lang.startsWith('en') ? 'Worn views. The product photo is shown when no matching scene is available.' : 'Vues portées. La photo produit reste affichée si aucune scène ne correspond.')
          : (document.documentElement.lang.startsWith('en') ? 'Compare designs and colours.' : 'Comparez les modèles et les couleurs.');
      }
    });
  }
  if (!customElements.get('brazur-inspirations')) {
    customElements.define('brazur-inspirations', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        const controls = this.querySelector('[data-gallery-controls]');
        if (!controls) return;
        this.initialized = true;
        this.cards = [...this.querySelectorAll('[data-scene]')];
        this.category = 'all';
        this.select = this.querySelector('[data-gallery-model]');
        this.buttons = [...this.querySelectorAll('[data-category]')];
        const models = new Map(this.cards.map(card => [card.dataset.model, card.dataset.modelTitle]));
        for (const [value, title] of [...models].sort((a, b) => a[1].localeCompare(b[1], 'fr'))) {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = title;
          this.select.append(option);
        }
        for (const button of this.buttons) {
          button.addEventListener('click', () => {
            this.category = button.dataset.category;
            const available = new Set(this.cards.filter(card => this.category === 'all' || card.dataset.family === this.category).map(card => card.dataset.model));
            for (const option of this.select.options) {
              option.disabled = option.value !== 'all' && !available.has(option.value);
              option.hidden = option.disabled;
            }
            if (this.select.value !== 'all' && !available.has(this.select.value)) this.select.value = 'all';
            this.apply();
          });
        }
        this.select.addEventListener('change', () => this.apply());
        controls.hidden = false;
        this.apply();
      }
      apply() {
        let count = 0;
        for (const card of this.cards) {
          const matches = (this.category === 'all' || card.dataset.family === this.category)
            && (this.select.value === 'all' || card.dataset.model === this.select.value);
          card.hidden = !matches;
          if (matches) count++;
        }
        for (const button of this.buttons) button.setAttribute('aria-pressed', String(button.dataset.category === this.category));
        this.querySelector('[data-gallery-count]').textContent = `${count} mise${count === 1 ? '' : 's'} en situation`;
        this.querySelector('[data-gallery-empty]').hidden = count !== 0;
      }
    });
  }
})();
